import type { Catalog } from "../../en";

export const agentSettings: Catalog["build"]["agentSettings"] = {
  title: "Settings ng agent · {{name}}",
  subtitle: "Kontrolin kung paano magsalita ang assistant, ano ang alam nito, kailan ito magpapasa sa staff, at ano ang hindi nito dapat gawin kailanman.",
  testInPlayground: "Subukan sa Playground",
  more: "Iba pang aksyon",
  duplicate: "I-duplicate ang agent",
  duplicated: "Na-duplicate bilang “{{name}}” sa Draft",
  export: "I-export ang settings (JSON)",
  exported: "Na-export ang settings bilang {{file}}",
  history: "Kasaysayan ng bersyon",
  tabsLabel: "Mga seksyon ng settings",
  tabs: {
    general: "Pangkalahatan",
    instructions: "Mga tagubilin",
    model: "Model at mga sagot",
    retrieval: "Retrieval",
    knowledge: "Kaalaman",
    guardrails: "Guardrails",
    escalation: "Escalation at handoff",
    languages: "Mga wika",
    glossary: "Glossary",
    memory: "Memory at privacy",
    danger: "Mapanganib na bahagi",
  },
  adv: "Adv",
  appliesTitle: "Para sa lahat ng channel",
  appliesBody: "Ginagamit ng widget, web app, mobile app at Messenger ang settings na ito. Nasa Widget Studio ang itsura kada channel.",
  widgetStudio: "Widget Studio",
  saveBar: {
    saved: "Naka-save lahat ng pagbabago · {{time}}",
    savedNever: "Naka-save lahat ng pagbabago",
    dirty: "May hindi pa naka-save",
    dirtyHint: "· ilalapat sa lahat ng channel kapag na-save",
    discard: "Itapon",
    save: "I-save ang mga pagbabago",
    savedToast: "Na-save ang settings · live sa lahat ng channel",
    discarded: "Itinapon ang mga pagbabago",
    reindex: "Kailangan ng re-index ang mga pagbabago sa retrieval — pinoproseso ulit ang iyong mga source.",
    invalid: "May mga field na kailangang ayusin bago i-save.",
  },
  readOnly: "Makikita mo ang settings na ito pero hindi mo ito mababago.",
  publishNeeded: "Kailangan ng publish permission para mag-live",
  greeting: {
    title: "Welcome message",
    hint: "Ang unang mensahe ng assistant sa bawat chat — widget, web app at mobile app. Lalabas sa ilalim nito ang iyong mga starter question bilang chips.",
    label: "Welcome message",
    defaultNote: "Kapag walang laman = default na pagbati: “{{preview}}”",
    save: "I-save ang welcome message",
    saved: "Na-save ang welcome message · live sa lahat ng channel",
    savedDraft: "Na-save sa draft ng widget. May iba pang hindi pa na-publish na pagbabago ang widget — i-publish ang mga ito sa Widget Studio para mag-live.",
    startersNote: "Ang mga starter question ay ine-edit sa",
    studio: "Widget Studio",
    unavailable: "Hindi ma-load dito ang welcome message. Maaaring wala kang access sa Widget Studio.",
  },
  general: {
    identity: "Pagkakakilanlan",
    identityHint: "Kung paano lumalabas ang assistant sa mga taong tinutulungan nito.",
    name: "Pangalan ng agent",
    nameHint: "Ipinapakita sa header ng chat at sa inbox.",
    nameRequired: "Maglagay ng pangalan.",
    agentId: "Agent ID",
    agentIdHint: "Ginagamit sa widget snippet at sa API.",
    copyId: "Kopyahin ang agent ID",
    avatar: "Avatar",
    avatarHint: "Pumili ng preset. Lumalabas ito sa header ng chat at sa mga mensahe.",
    avatarPresets: { bot: "Icon ng bot", initials: "Inisyal", sparkles: "Icon ng sparkles", building: "Icon ng gusali", headset: "Icon ng headset" },
    description: "Paglalarawan",
    descriptionHint: "Panloob lang — tumutulong sa team na makilala ang mga agent.",
    availability: "Availability",
    status: "Status",
    statusHint: "Offline message ang ipinapakita ng mga naka-pause na agent. Sa Playground lang makikita ang mga draft.",
    audience: "Audience",
    audienceHint: "Sino ang puwedeng makipag-chat sa agent na ito. Sa Audiences at Access sine-set up ang mga naka-sign-in na audience.",
    audienceLink: "Audiences at Access",
    audiencePublicHint: "Ang mga source lang na para sa pampublikong audience ang makikita ng mga pampublikong bisita.",
    audienceRequired: "Pumili ng kahit isang audience.",
  },
  instructions: {
    role: "Papel at persona",
    roleHint: "Ang system prompt. Isinulat sa simpleng wika — hindi kailangan ng coding.",
    promptLabel: "System instructions",
    count: "{{count}} / {{max}} character",
    variables: "Mga prompt variable",
    variablesHint: "· i-click para ipasok sa cursor",
    variableEmpty: "walang laman hangga't hindi alam",
    inserted: "Naipasok ang {{variable}}",
    unknown: "Hindi kilalang variable: {{names}} — mananatili silang walang laman.",
    style: "Estilo",
    tone: "Tono",
    toneHint: "Pumili ng hanggang tatlo.",
    tones: { warm: "Magiliw", professional: "Propesyonal", cheerful: "Masigla", respectful: "Magalang (po/opo)", formal: "Pormal", encouraging: "Nakapagpapalakas-loob" },
    length: "Haba ng sagot",
    lengthHint: "Katamtaman ≈ 60–120 salita. Hindi pinuputol ang listahan ng mga requirement.",
    lengths: { short: "Maikli", medium: "Katamtaman", detailed: "Detalyado" },
  },
  model: {
    title: "AI model",
    hint: "Ibinabawas ang credits sa iyong buwanang message allowance.",
    usage: "Paggamit at credits",
    recommended: "Inirerekomenda",
    tier: { standard: "Standard", premium: "Premium" },
    credits_one: "{{count}} credit / mensahe",
    credits_other: "{{count}} credits / mensahe",
    notInPlan: "Wala sa iyong plano",
    noModels: "Wala pang available na model.",
    noModelsHint: "Naka-off muna ang mga AI provider. Subukan ulit mamaya o makipag-ugnayan sa support.",
    unavailable: "Hindi available ang {{model}}; ginagamit ang {{fallback}}.",
    unavailableUnknown: "Ang naka-save na model",
    unavailableError: "Hindi available ngayon ang AI model na iyon. Pumili ng ibang model.",
    temperature: "Pagkamalikhain (temperature)",
    precise: "Eksakto",
    creative: "Malikhain",
    maxTokens: "Pinakamahabang sagot (tokens)",
    maxTokensHint: "≈ {{words}} salita. Mas mahal ang mahahabang sagot at bihirang basahin sa mobile.",
    maxTokensRange: "Sa pagitan ng 100 at 4,000, kada 50.",
    quality: "Kalidad ng sagot",
    strict: "Mahigpit na grounding — sumagot lang mula sa knowledge",
    strictHint: "Hindi gagamit ang assistant ng pangkalahatang kaalaman. Inirerekomenda para sa presyo, petsa at patakaran.",
    citation: "Estilo ng citation",
    citationHint: "Walang numero sa mga sagot bilang default; nakalista sa ilalim ng bawat sagot ang mga pinagkunan nito.",
    citations: { numbered: "May numerong [1] sa loob ng sagot, kasama ang listahan ng source", source_names: "Mga source sa ilalim ng sagot (inirerekomenda)", hidden: "Nakatago (makikita pa rin ng staff sa Inbox)" },
    idk: "Ipakita ang “Hindi ko alam” sa halip na manghula",
    idkHint: "Kapag mababa sa threshold ang kumpiyansa, ipapakita ang fallback message at itatala ang tanong bilang Knowledge Gap.",
    confidence: "Threshold ng kumpiyansa",
    answerMore: "Mas madalas sumagot",
    onlySure: "Kapag sigurado lang",
    fallback: "Fallback message",
    followups: "Magmungkahi ng follow-up na tanong",
    followupsHint: "Nagpapakita ng 2–3 tanong na puwedeng i-tap pagkatapos ng bawat sagot.",
  },
  retrieval: {
    info: "Naka-tune na ang mga default; baguhin lang kung may namimiss na impormasyon ang mga sagot.",
    infoBody: "Pagkatapos ng anumang pagbabago, magpatakbo ng evaluation para ikumpara sa iyong test set bago mag-live.",
    chunking: "Chunking",
    chunkingHint_one: "Kung paano hinahati ang mga dokumento bago i-embed. Mare-re-index ang {{count}} source kapag binago ito.",
    chunkingHint_other: "Kung paano hinahati ang mga dokumento bago i-embed. Mare-re-index ang {{count}} source kapag binago ito.",
    chunkSize: "Laki ng chunk",
    chunkSizes: { "256": "256 tokens — maiikling FAQ", "512": "512 tokens — inirerekomenda", "1024": "1024 tokens — mahahabang patakaran" },
    overlap: "Overlap",
    overlapHint: "Napapanatiling mahahanap ang mga pangungusap na tumatawid sa hangganan ng chunk.",
    search: "Paghahanap at ranking",
    searchHint: "Hybrid search (keyword + vectors) na pinagsama sa Reciprocal Rank Fusion, saka nire-rerank.",
    topK: "Top-K na kandidato",
    topKHint: "Mga chunk na kinukuha bago mag-rerank.",
    final: "Huling context chunks",
    finalHint: "Mga chunk na talagang ipinapadala sa model.",
    hybrid: "Hybrid weighting",
    hybridValue: "{{keyword}} / {{meaning}} · RRF",
    keyword: "Keyword (eksaktong termino)",
    meaning: "Kahulugan (paraphrase)",
    rerank: "I-rerank ang resulta",
    rerankHint: "Muling minamarkahan ng cross-encoder ang mga kandidato. Karaniwang mas tumpak; may kaunting dagdag na latency.",
    rerankModel: "Ang platform ang namamahala sa reranker model.",
    threshold: "Similarity threshold",
    thresholdHint: "Tinatanggal ang mga chunk na mas mababa rito.",
    advanced: "Advanced",
    kDense: "Mga vector na kandidato",
    kLexical: "Mga keyword na kandidato",
    perSource: "Pinakamaraming chunk kada source",
    perSourceHint: "Pinipigilan ang isang mahabang dokumento na matabunan ang iba.",
    neighbors: "Neighbor expansion",
    neighborsHint: "Idinadagdag ang mga chunk bago at pagkatapos ng tugma para sa mas maraming konteksto.",
    graph: "Knowledge graph",
    graphStage: "Experiment",
    graphHint: "Nagdadagdag ng ikalawang paraan para makahanap ng magkakaugnay na bahagi: mga entity at ang ugnayan nila, kinuha mula sa aktibo mong knowledge. Sa mga source mo pa rin galing ang sagot at pareho pa rin ang audience rules.",
    understanding: "Pag-unawa sa tanong",
    rewrite: "Query rewriting",
    rewriteHint: "Ginagawang buong tanong ang mga follow-up gaya ng “paano naman sa susunod na taon?” gamit ang kasaysayan ng chat.",
    contextual: "Contextual retrieval",
    contextualHint: "Nagdadagdag ng maikling pangungusap ng konteksto sa bawat chunk bago i-embed. Mas kaunting namimiss sa mga table at listahan.",
    reindex: "Re-index",
    multiQuery: "Multi-query expansion",
    multiQueryHint: "Hinahanap ang ilang bersyon ng tanong (English, Filipino, keyword). Nakakatulong sa Taglish; may dagdag na latency.",
    filters: "Awtomatikong inilalapat ang metadata filters.",
    filtersBody: "Sinasala ang bawat paghahanap ayon sa access level ng naka-sign-in na user, at puwedeng paliitin ayon sa uri ng dokumento at petsa ng bisa para hindi magamit ang mga lumang dokumento. I-set ang mga ito kada source sa Knowledge Base.",
    knowledgeLink: "Knowledge Base",
    lastEval: "Huling evaluation:",
    lastEvalValue: "{{date}} · {{name}} · {{pass}}/{{total}} ang pumasa",
    lastEvalNone: "Wala pang evaluation para sa agent na ito.",
    runEval: "Magpatakbo ng evaluation gamit ang settings na ito",
  },
  guardrails: {
    blocked: "Mga bawal na paksa",
    blockedHint: "Magalang na tatanggi ang assistant at ibabaling sa mga paksang matutulungan nito.",
    blockedLabel: "Bawal na paksa",
    blockedPlaceholder: "Mag-type ng paksa at pindutin ang Enter",
    remove: "Alisin ang {{name}}",
    removed: "Inalis ang “{{name}}”",
    pii: "Personal na datos (Data Privacy Act 2012)",
    piiHint: "Tinatakpan ang mga natukoy na value sa transcripts, analytics at model logs.",
    npc: "Ayon sa NPC",
    piiKinds: {
      mobile: "Mga mobile number",
      email: "Mga email address",
      national_id: "National ID / member number",
      address: "Mga address ng tirahan",
      card_bank: "Mga numero ng card at bangko",
      gov_id: "Mga government ID (TIN, SSS…)",
    },
    piiExamples: { mobile: "0917 ••• ••42", email: "r•••@gmail.com", national_id: "1072•••••••3", address: "Blk •• Lot ••, Brgy. •••", card_bank: "4111 •••• •••• 1111", gov_id: "123-•••-•••-000" },
    redact: "I-redact ang {{name}}",
    safety: "Kaligtasan",
    injection: "Proteksyon sa prompt injection",
    injectionHint: "Binabalewala ang mga tagubiling nakatago sa mga mensahe o na-upload na dokumento.",
    profanity: "Filter ng pagmumura",
    profanityHint: "Pagmumura sa English at Filipino. Tinatapos ang chat at fina-flag sa Inbox kapag paulit-ulit ang pang-aabuso.",
    policies: {
      no_guarantees: "Huwag mangako ng resulta o pagtanggap",
      no_guaranteesHint: "Hinaharangan ang mga pariralang nangangako ng pag-apruba, pagtanggap o tiyak na resulta.",
      no_medical_diagnosis: "Walang medikal na diagnosis",
      no_medical_diagnosisHint: "Tumatangging mag-diagnose at nag-aalok na mag-book ng appointment.",
      no_legal_advice: "Walang legal na payo",
      no_legal_adviceHint: "Tumatanggi sa legal na interpretasyon at itinuturo ang tamang opisina.",
      no_financial_advice: "Walang payong pinansyal",
      no_financial_adviceHint: "Tumatanggi sa payo tungkol sa investment o credit.",
    },
    rules: "Mga custom na patakaran",
    rulesHint: "Sinusuri sa bawat sagot bago ipadala.",
    addRule: "Magdagdag ng patakaran",
    rule: "Patakaran {{n}}",
    moveUp: "Itaas ang patakaran {{n}}",
    moveDown: "Ibaba ang patakaran {{n}}",
    removeRule: "Alisin ang patakaran {{n}}",
    noRules: "Wala pang custom na patakaran.",
  },
  escalation: {
    triggers: "Ipasa sa tao kapag…",
    triggersHint: "Lilipat sa Inbox ang usapan at aabisuhan ang staff.",
    askHuman: "Humingi ang user ng tao",
    askHumanHint: "“Puwede ba akong makausap ng tao?”, “pwede po ba makausap ang tao?”",
    lowConfidence: "Mababang kumpiyansa nang ilang beses sunod-sunod",
    lowConfidenceHint: "Sunod-sunod na fallback na sagot bago ipasa.",
    strikes: "Sunod-sunod na fallback na sagot",
    payment: "Mga paksa tungkol sa bayad, refund o balanse",
    paymentHint: "Bayad na hindi pumasok, refund, kinukuwestiyong singil.",
    sentiment: "Negatibong damdamin",
    sentimentHint: "Natukoy na pagkadismaya o reklamo.",
    keywords: "Mga keyword",
    keywordsHint: "Hiwalay sa kuwit, hal. emergency, complaint, reklamo, refund.",
    routing: "Sino ang tatanggap ng handoff",
    routingHint: "Ganito ang pagkakasunod ng routing: payment team, team na tugma ang mga paksa, saka ang default sa ibaba.",
    routeTo: "Ipasa ang mga handoff sa",
    routeTeam: "Isang team",
    routeTeamHint: "Sinumang nasa team ay puwedeng sumagot. Kapag naka-on ang round-robin, mapupunta ito sa susunod na malayang miyembro.",
    routePerson: "Isang tao",
    routePersonHint: "Sa iisang miyembro mapupunta ang lahat ng handoff.",
    team: "Default na team",
    teamHint: "Gumawa ng mga team at paksa nila sa Team.",
    person: "Tao",
    personHint: "Mga aktibong miyembro lang na puwedeng sumagot sa Inbox ang mapipili.",
    choosePerson: "Pumili ng miyembro",
    personRequired: "Piliin kung sino ang tatanggap ng mga handoff.",
    membersUnavailable: "Hindi ma-load ang mga miyembro — kailangan mo ng access sa Team para pumili ng tao.",
    paymentTeam: "Payment team",
    paymentTeamHint: "Dito mapupunta ang mga paksa ng bayad at refund bago ang default.",
    paymentTeamOff: "I-on ang “Mga paksa tungkol sa bayad, refund o balanse” sa itaas para magamit ang payment team.",
    matchTopics: "Itugma ang mga paksa ng team",
    matchTopicsHint: "Ang team na tugma ang routing topics sa usapan ang unang makakakuha nito.",
    fallbackNote: "Kung walang team o taong naka-set (o hindi sila maabot), mapupunta ang mga handoff sa Owner at mga Admin na nakakakita ng inbox na ito.",
    sameAsDefault: "Kapareho ng default",
    noTeam: "Walang team — Owner at mga Admin",
    teamsUnavailable: "Hindi ma-load ang mga team — kailangan mo ng access sa Team para baguhin ang routing.",
    membersCount_one: "{{count}} miyembro sa team na ito.",
    membersCount_other: "{{count}} miyembro sa team na ito.",
    collect: "Humingi ng email kapag sarado ang opisina",
    collectHint: "Hihingi ang assistant ng email address pagkatapos ng oras para maabot ng team ang bisita.",
    reminder: "Paalalahanan ang team pagkatapos ng (minuto)",
    reminderHint: "Kung walang sumagot sa loob ng {{minutes}} minuto, makakatanggap ng paalala ang team at ang lead nito; pagkalipas ng {{escalate}} minuto, ipapaalam sa Owner at mga Admin. Tumatakbo lang ang paalala sa oras ng opisina.",
    reminderHintShort: "Maglagay ng buong minuto. Ipapaalam sa Owner at mga Admin pagkalipas ng doble nito.",
    reminderRange: "Maglagay ng buong bilang mula {{min}} hanggang {{max}}.",
    hours: "Oras ng opisina",
    hoursHint: "{{timezone}}. Ginagamit para sa {{variable}} at availability ng handoff.",
    day: "Araw",
    open: "Bukas",
    from: "Mula",
    to: "Hanggang",
    openLabel: "Bukas ang {{day}}",
    fromLabel: "Oras ng pagbukas sa {{day}}",
    toLabel: "Oras ng pagsara sa {{day}}",
    days: { mon: "Lunes", tue: "Martes", wed: "Miyerkules", thu: "Huwebes", fri: "Biyernes", sat: "Sabado", sun: "Linggo" },
    hoursInvalid: "Dapat mas maaga ang pagbukas kaysa pagsara.",
    outOfHours: "Mensahe sa labas ng oras",
  },
  mandatory: {
    title: "Mga required na handover",
    hint: "Galing sa industry pack na ginamit ninyo. Laging napupunta ang mga ito sa tao: hindi ito kailanman nilulutas ng assistant, at walang flow o tier na makakapag-patay sa kanila.",
    rulesScroll: "Mga required na handover, maaaring i-scroll",
    tiersScroll: "Mga priyoridad ng tier, maaaring i-scroll",
    rulesLabel: "Mga required na handover mula sa pack",
    colRule: "Patakaran",
    colDetected: "Paano natutukoy",
    colPriority: "Priyoridad",
    colOutOfHours: "Labas ng oras",
    locked: "Naka-lock: hindi maaaring patayin",
    beforeTiers: "nauuna sa lahat ng tier",
    trigger: {
      keywords: "Mga salita",
      model_signal: "Mga salita at pagbasa ng assistant",
      person_request: "Humihingi ng tao",
      low_confidence: "Sunud-sunod na mababang kumpiyansa",
      negative_sentiment: "Negatibong damdamin",
    },
    priority: { urgent: "Urgent", high: "Mataas", normal: "Normal", low: "Mababa" },
    outHelpline: "Helpline",
    outTicket: "Urgent na ticket",
    outOnCall: "Alerto sa naka-on-call",
    outNone: "Karaniwang pila",
    neverChanges: "Hindi kailanman binabago ng assistant ang account (password, payout details, ID data, limit) at hindi nito isinasagawa ang exclusion request: ipinapasa ito sa tao.",
    noRules: "Walang nakalistang required na handover ang pack.",
    emptyTitle: "Wala pang industry pack na nailapat",
    emptyBody: "Maglapat ng industry pack sa Service settings para ma-load ang mga required na handover at priyoridad ng tier. Maaari pa ring itakda sa ibaba kung sino ang tatanggap.",
    errorTitle: "Hindi ma-load ang mga patakaran ng pack",
    errorBody: "Hindi mabasa ang mga required na handover. Umiiral pa rin ang mga setting sa ibaba.",
    retry: "Subukan muli",
    tiersTitle: "Mga tier at priyoridad",
    tiersHint: "Ang tier ay galing lang sa ipinadala ng inyong system tungkol sa naka-sign in na customer. Ang pinakamataas na tier ay lalaktaw sa menu at mapupunta sa pinakakaunting trabaho sa priority team; laging nauuna ang required na handover sa itaas.",
    tiersLabel: "Mga priyoridad ng tier mula sa pack",
    colTier: "Tier",
    colMenu: "Laktawan ang menu ng bot",
    colBasePriority: "Batayang priyoridad",
    colPickup: "Target na pagsagot",
    skipMenu: "Oo",
    noSkip: "Hindi",
    minutes_one: "{{count}} min",
    minutes_other: "{{count}} min",
    team: "Team para sa required na handover",
    teamHint: "Dito pupunta ang required na handover. Kapag walang pinili, gagamitin ang default na ruta sa itaas.",
    onCall: "Naka-on-call",
    onCallHint: "Aalertuhan agad kapag may required na handover sa labas ng oras ng opisina.",
    noOnCall: "Wala (aalertuhan ang team gaya ng dati)",
    noOnCallWarning: "Walang naka-on-call. Sa labas ng oras ng opisina, magbubukas pa rin ng urgent na ticket ang required na handover, pero walang maaalertuhan agad. Pumili ng on-call na contact sa ibaba.",
    helpline: "Helpline na makikita ng customer",
    helplineHint: "Makikita sa sagot sa required na handover, halimbawa ang numero ng pambansang helpline. {{count}}/{{max}}",
    priorityTeam: "Priority team",
    priorityTeamHint: "Ang customer sa pinakamataas na tier na humihingi ng tao ay mapupunta sa pinakakaunting trabaho sa team na ito.",
    noPriorityTeam: "Walang priority team (karaniwang ruta)",
  },
  languages: {
    title: "Mga wikang naiintindihan ng assistant",
    hint: "Pumili ng kahit isa.",
    names: { en: "English", fil: "Filipino", taglish: "Taglish auto-detect", pam: "Kapampangan" },
    codes: { en: "EN", fil: "FIL", taglish: "TL", pam: "KAP" },
    descriptions: {
      en: "Pangunahing wika ng karamihan ng dokumento",
      fil: "Sumasagot nang may po/opo kapag Filipino ang gamit ng user",
      taglish: "Naiintindihan bilang iisang tanong ang halong English–Filipino",
      pam: "Naiintindihan ang simpleng tanong; sumasagot sa English o Filipino",
    },
    beta: "Beta",
    required: "Panatilihing naka-on ang kahit isang wika.",
    reply: "Sumagot sa wika ng user",
    replyHint: "Kapag naka-off, laging English ang sagot ng assistant.",
    noTranslate: "Hindi kailangang isalin ang iyong knowledge.",
    noTranslateBody: "Nananatili ang mga dokumento; itinutugma ayon sa kahulugan ang mga tanong sa Filipino o Taglish, at isinusulat ang sagot sa wika ng user na may orihinal na source. Hindi isinasalin ang presyo, petsa at pangalan ng dokumento.",
  },
  memory: {
    title: "Datos ng usapan",
    hint: "Saklaw ng iyong Data Processing Agreement.",
    keep: "Itago ang mga usapan nang",
    keepHint: "Binubura o ina-anonymize gabi-gabi ang mga transcript na mas luma rito.",
    workspaceDefault: "Default ng workspace",
    days: "{{count}} araw",
    remember: "Tandaan ang mga naka-sign-in na user sa bawat session",
    rememberHint: "Maipagpapatuloy ng naka-sign-in na user ang usapan noong nakaraang linggo. Hindi natatandaan ang anonymous na bisita nang lampas sa ilang araw.",
    anonymize: "I-anonymize sa halip na burahin pagkatapos ng retention",
    anonymizeHint: "Inaalis ang pangalan, numero at email; itinatago ang mga tanong para sa analytics at Knowledge Gaps.",
    dsr: "Mga data subject request",
    dsrHint: "I-export o burahin ang lahat tungkol sa isang tao (gamit ang email o mobile).",
    dsrOpen: "Bagong request",
  },
  danger: {
    title: "Mapanganib na bahagi",
    pause: "I-pause ang agent",
    resume: "Ituloy ang agent",
    pauseHint: "Mensahe sa labas ng oras ang ipapakita ng lahat ng channel. Walang mabubura.",
    paused: "Naka-pause ang {{name}}",
    resumed: "Live na ulit ang {{name}}",
    delete: "Burahin ang agent",
    deleteHint: "Binubura ang agent at pinahihinto ang widget, Messenger page at chat link nito. Naiiwan ang mga knowledge source.",
    deleteTitle: "Burahin ang {{name}}?",
    deleteBody: "Hindi na ito maibabalik. Mabubura ang mga usapan, lead at settings pagkatapos ng grace period sa iyong retention policy.",
    deleteConfirm: "I-type ang {{name}} para kumpirmahin",
    deleted: "Nabura ang agent",
  },
  versions: {
    title: "Kasaysayan ng bersyon",
    hint: "Mga nai-publish na bersyon. Ang pag-restore ay ilalapat ang settings ng bersyong iyon bilang kasalukuyang configuration.",
    current: "Kasalukuyan",
    restore: "I-restore",
    restored: "Na-restore ang bersyon {{version}}",
    publish: "I-publish ang kasalukuyang settings",
    published: "Nai-publish ang bersyon {{version}}",
    empty: "Wala pang nai-publish na bersyon",
    emptyBody: "I-publish ang agent para magawa ang unang bersyon.",
    version: "Bersyon {{version}}",
    by: "{{user}} · {{time}}",
    someone: "Isang miyembro ng team",
    unpublished: "Naka-save",
  },
};
